import { AsyncConstructor } from '../../../../../webmodule-common/other/async-constructor';
import { CSVProducerData } from '../../../../api/dealer-api-interface-franchisee';
import { firstValidString } from '../../../../../webmodule-common/other/ui/string-helper-functions';
import { getApiFactory } from '../../../../api/api-injector';
import { html } from 'lit';
import { information } from '../../../../../webmodule-common/other/ui/modal-option';
import {
  LitModalScreenEvents,
  QuoteItemProvider,
  V6QuoteFrameBomViewer
} from '../../../../../webmodule-common/interop/webmodule-interop';
import { litModalScreenFactory } from '../../../../../webmodule-common/other/ui/modal/modal-factory-lit';
import { lockUIandExecute } from '../../../../../webmodule-common/other/ui-lock';
import { nowServer } from '../../../../../webmodule-common/other/datetime-converter';

import { v6Editors } from '../../../../v6config/v6config';
import { LangFranchiseeV6BomViewer } from '../../../../../localization/scripts/dealer-franchisee/quotes/views/v6/franchisee-v6-bom-viewer-lang';

export class V6BomViewer implements AsyncConstructor, LitModalScreenEvents {
  v6QuoteItemData: QuoteItemProvider;
  bomViewer?: V6QuoteFrameBomViewer | null;
  modal = litModalScreenFactory(this);
  titleStr: string;
  constructor(title: string, v6QuoteItemData: QuoteItemProvider) {
    this.titleStr = title;
    this.v6QuoteItemData = v6QuoteItemData;
  }
  afterConstructionExecuted?: boolean | undefined;
  async afterConstruction(): Promise<void> {
    this.bomViewer = await v6Editors().getBomViewer(this.v6QuoteItemData);
  }
  modalSize = 'modal-fullscreen modal-v6-bom-view';
  bodyTemplate() {
    return html`${this.bomViewer?.ui}`;
  }
  requestUpdate() {
    this.modal.requestUpdate();
  }
  footerTemplate() {
    const eventClose = async () => {
      await this.hideModal();
    };
    const eventExport = async () => {
      await lockUIandExecute(async () => {
        await this.exportCsv();
      });
    };
    return html` <webmodule-button size="small" variant="default" @click=${eventClose}
        >${LangFranchiseeV6BomViewer.close()}</webmodule-button
      >
      <webmodule-button size="small" variant="primary" @click=${eventExport}
        >${LangFranchiseeV6BomViewer.export()}</webmodule-button
      >`;
  }

  private convertRows(rows: any[], metaData: any): { [key: string]: any }[] {
    return rows.map(item => {
      const { nested_data, ...csvData } = item;
      if (nested_data) {
        csvData['_NestedDataRows'] = this.convertNested(nested_data, metaData);
      }
      return csvData;
    });
  }
  private convertNested(nestedData, metaData: any) {
    return this.convertBomUIDataToCSVProducerData(nestedData, metaData);
  }

  private convertBomUIDataToCSVProducerData(data: any, metaData: any): CSVProducerData[] {
    const result: CSVProducerData[] = [];
    for (const key in data) {
      const meta = metaData.find(x => x.resourceName === key);
      const filename = `${firstValidString(meta.tabCaption, meta.TabCaption, meta.resourceName)}.csv`;
      const rows = data[key];
      const csvD: CSVProducerData = {
        columns: meta.fields.map(x => x.fieldName),
        rows: this.convertRows(rows, metaData),
        fileName: filename
      };
      result.push(csvD);
    }
    return result;
  }
  async exportCsv() {
    const filename = `bomview ${this.titleStr} - ${nowServer()}.csv`;
    const bv = globalThis.debug_bomView ?? (this.v6QuoteItemData.quoteItem as any).bomView;
    let inputs: CSVProducerData[] = [];

    const version = bv['version'];
    if (version && version === 'ui') {
      inputs = this.convertBomUIDataToCSVProducerData(bv.data, bv.metaData);
      //todo new export
    } else {
      const types = bv.metaData.map(x => x.resourceName);
      inputs = types.map(rtype => {
        const fields = bv.metaData.find(x => x.resourceName === rtype).fields.map(f => f.fieldName);
        const csvD: CSVProducerData = {
          columns: fields ?? [],
          rows: bv[rtype],
          fileName: `${rtype}.csv`
        };
        return csvD;
      });
      //old export
    }

    const extCsv = await getApiFactory().franchisee().convertToCsv({ fileName: 'bomview.csv', data: inputs });
    if (extCsv?.status !== 200) {
      await information(LangFranchiseeV6BomViewer.errorConvertingToCsv());
      return;
    }
    const url = URL.createObjectURL(extCsv.data);
    try {
      const pom = document.createElement('a');
      pom.setAttribute('href', url);
      pom.setAttribute('download', filename);
      const event = new Event('click');
      pom.dispatchEvent(event);
      pom.click();
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  title = () => LangFranchiseeV6BomViewer.bomViewTitle({ title: this.titleStr });
  isFooterVisible = true;
  async showModal() {
    const bv = (this.v6QuoteItemData.quoteItem as any).bomView;
    if (!bv) {
      await information(LangFranchiseeV6BomViewer.noBomDataForQuoteItem());
      return;
    }
    // eslint-disable-next-line consistent-return
    return await this.modal.showModal();
  }
  async hideModal() {
    return await this.modal.hideModal();
  }
}
